// ===== rules/rule-0018-0019-bmi-neonate.ts ================================
import { BaseRule } from '../rule-base';
import { AdjustedLine, ModifierContext } from '../modifier-models';

/**
 * 0018 (BMI ≥ 35)  and 0019 (neonate / LBW) – both add +50 %:
 *   • surgeons: +50 % on every *procedure line* carrying the modifier
 *   • anaesthetists: +50 % on the 0023 "time line" in the claim
 *
 * The BMI modifier (0018) is a standalone line that calculates its own value
 * based on the total price of non-modifier lines.
 */
export class RuleBmiNeonate50 extends BaseRule {
  name = '0018 / 0019  +50 %';

  private readonly codes = new Set(['0018', '0019']);

  run(line: AdjustedLine, ctx: ModifierContext, autoApply: boolean): void {
    // Handle BMI modifier as standalone line
    if (line.isModifier && !line.isConsult && line.code === '0018') {
      const bmi = parseFloat(line.BMI?.toString() || '0');
      if (bmi <= 35) {
        line.trace.push('0018: BMI not > 35, no increase applied');
        return;
      }
      if (autoApply) {
        const total = ctx.totalPrice || 0;
        const increase = total * 0.5;
        line.adjustedUnits = 1;
        line.adjustedRand = +(increase).toFixed(2);
        line.trace.push(`0018: BMI > 35, applying 50% increase (R${increase.toFixed(2)})`);
      } else {
        line.suggestions = line.suggestions || [];
        line.suggestions.push({
          ruleCode: '0018',
          message: '50% increase for BMI ≥ 35',
          kind: 'overwrite'
        });
      }
      return;
    }

    // Handle neonate modifier (0019)
    if (line.isModifier && !line.isConsult && line.code === '0019') {
      if (autoApply) {
        line.trace.push('0019: neonate/LBW condition met');
      } else {
        line.suggestions = line.suggestions || [];
        line.suggestions.push({
          ruleCode: '0019',
          message: '50% increase for neonate/LBW',
          kind: 'overwrite'
        });
      }
    }

    // Handle anaesthetic time line
    if (line.code === '0023') {
      if (autoApply) {
        line.trace.push('0018/0019: Requires full context for time line calculation');
      } else {
        line.suggestions = line.suggestions || [];
        line.suggestions.push({
          ruleCode: '0018/0019',
          message: 'Time line calculation requires full context',
          kind: 'overwrite'
        });
      }
    }
  }

  apply(lines: AdjustedLine[], ctx: ModifierContext): AdjustedLine[] {
    // BMI modifier as standalone line
    const bmiModifier = lines.find(l => l.code === '0018' && l.isModifier);
    if (bmiModifier) {
      const bmi = parseFloat(bmiModifier.BMI?.toString() || '0');
      if (bmi > 35) {
        const total = ctx.totalPrice || 0;
        const increase = total * 0.5;
        bmiModifier.adjustedUnits = 1;
        bmiModifier.adjustedRand = +(increase).toFixed(2);
        bmiModifier.trace.push(`0018: BMI > 35, applying 50% increase (R${increase.toFixed(2)})`);
      } else {
        bmiModifier.trace.push('0018: BMI not > 35, no increase applied');
      }
    }

    // Neonate modifier as standalone line
    const neonateModifier = lines.find(l => l.code === '0019' && l.isModifier);
    if (neonateModifier) {
      neonateModifier.trace.push('0019: neonate/LBW condition met');
    }

    // Anaesthetic time line (0023) - just add a trace for now
    const timeLine = lines.find(l => l.code === '0023');
    if (timeLine) {
      timeLine.trace.push('0018/0019: Requires full context for time line calculation');
    }

    return lines;
  }
}
